import PropTypes from 'prop-types'
import React, { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'

import { getRegion } from 'models/General/selectors'
import { getUserAlias } from 'models/ProfileInfo/selectors'
import { STRING } from 'resources'
import { getReklaimProtectUserProfile, setReklaimProtectUserProfile } from 'services/reklaimProtect'

import { getFieldConfig } from '../constants'

import { Form, InputRow, Label, InputContainer, ButtonsContainer, ConfirmButton } from './styles'

const UserProfileSection = ({ userProfileData, setUserProfileData, setErrorMessage, setProfileInfo, toggle }) => {
  const userAlias = useSelector(getUserAlias)
  const region = useSelector(getRegion)
  const fieldConfig = getFieldConfig(region)

  const [formErrors, setFormErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)

  const handleChange = useCallback(
    (e, key, config) => {
      const val = e.target.value
      const updatedUserProfileData = { ...userProfileData, [key]: val }
      setUserProfileData(updatedUserProfileData)
      const isValid = config.validate(val, region)
      if (!isValid && hasSubmitted) {
        const fieldError = val !== '' ? config.errorMessage : STRING.protect.profileInfo.edit.error.requiredField
        setFormErrors((prev) => ({
          ...prev,
          [key]: fieldError,
        }))
      } else {
        setFormErrors((prev) => ({
          ...prev,
          [key]: null,
        }))
      }
    },
    [userProfileData, region, hasSubmitted, setUserProfileData],
  )

  const onSubmit = useCallback(async () => {
    setHasSubmitted(true)
    setErrorMessage(null)
    setIsSubmitting(true)

    const newFormErrors = {}
    Object.keys(userProfileData).forEach((key) => {
      const config = fieldConfig[key]
      const isValid = config.validate(userProfileData[key], region)
      newFormErrors[key] = !isValid ? config.errorMessage : null
    })
    setFormErrors(newFormErrors)

    if (Object.values(newFormErrors).some(Boolean)) {
      setIsSubmitting(false)
      return
    }

    try {
      await setReklaimProtectUserProfile(userAlias, userProfileData)
      const updatedProfileInfo = await getReklaimProtectUserProfile(userAlias)
      setProfileInfo((prev) => ({
        ...prev,
        userProfile: updatedProfileInfo,
      }))
      toggle()
    } catch (error) {
      setErrorMessage(STRING.protect.profileInfo.edit.error.updateFailed)
    } finally {
      setIsSubmitting(false)
    }
  }, [
    fieldConfig,
    region,
    userProfileData,
    userAlias,
    setProfileInfo,
    toggle,
    setIsSubmitting,
    setErrorMessage,
    setFormErrors,
    setHasSubmitted,
  ])

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit()
      }}
    >
      {Object.entries(fieldConfig).map(([key, config]) => (
        <InputRow key={key}>
          <Label>{config.label}</Label>
          <InputContainer
            type={config.type || 'text'}
            placeholder={config.placeholder}
            value={userProfileData[key]}
            onChange={(e) => handleChange(e, key, config)}
            invalid={!!formErrors[key]}
            errorMessage={formErrors[key]}
            disabled={isSubmitting}
          />
        </InputRow>
      ))}
      <ButtonsContainer>
        <ConfirmButton
          onPress={onSubmit}
          isLoading={isSubmitting}
          disabled={isSubmitting || Object.values(formErrors).some(Boolean)}
          type="submit"
        >
          {STRING.protect.profileInfo.edit.save}
        </ConfirmButton>
      </ButtonsContainer>
    </Form>
  )
}

UserProfileSection.propTypes = {
  userProfileData: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    address: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
    birthDate: PropTypes.string,
  }).isRequired,
  setUserProfileData: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  setProfileInfo: PropTypes.func.isRequired,
  toggle: PropTypes.func.isRequired,
}

export default UserProfileSection
